package fr.manyverse.app.infrastructure.datastore

data class StoredDiceConfig(val dieType: String, val count: Int, val modifier: Int)

interface UserPreferencesDataStore {
    suspend fun saveTermsAccepted(userId: String)
    suspend fun isTermsAccepted(userId: String): Boolean
    suspend fun saveThemePreference(isDark: Boolean?)
    suspend fun getThemePreference(): Boolean?

    /**
     * Persiste les IDs d'annonces déjà fermées par cet utilisateur pour cette table.
     * Sans ça, un kill process / fermeture force-quit ré-ouvre toutes les annonces
     * actives au prochain démarrage.
     */
    suspend fun saveDismissedAnnouncements(userId: String, tableId: String, eventIds: Set<String>)
    suspend fun getDismissedAnnouncements(userId: String, tableId: String): Set<String>

    /**
     * Timestamp (epoch ms) du dernier event vu par cet utilisateur pour cette table.
     * Permet, au prochain démarrage, de ne (re)montrer en notifications que les events
     * postérieurs à ce moment-là, sans rejouer ce que l'utilisateur a déjà vu.
     * `null` = jamais ouvert cette table.
     */
    suspend fun saveLastSeenAt(userId: String, tableId: String, epochMillis: Long)
    suspend fun getLastSeenAt(userId: String, tableId: String): Long?

    /**
     * Persiste la dernière config de dés choisie par cet utilisateur pour cette table
     * (dé, quantité, modificateur). Restaurée au retour sur la table.
     */
    suspend fun saveDiceConfig(userId: String, tableId: String, dieType: String, count: Int, modifier: Int)
    suspend fun getDiceConfig(userId: String, tableId: String): StoredDiceConfig?

    /**
     * Persiste la couleur de feutre de la piste de dés (cosmétique, globale, non liée à une table).
     * Restaurée au prochain lancement de l'app.
     */
    suspend fun saveDiceTrayColor(colorId: String)
    suspend fun getDiceTrayColor(): String?

    /**
     * Persiste la couleur des dés 3D (cosmétique, globale, non liée à une table).
     * Restaurée au prochain lancement de l'app.
     */
    suspend fun saveDiceColor(colorId: String)
    suspend fun getDiceColor(): String?

    /**
     * Persiste l'image portée par la face « 1 » des dés (cosmétique, globale, non liée à une
     * table). Absente tant que l'utilisateur n'a rien choisi : le style de face de l'univers
     * sert alors de valeur initiale.
     */
    suspend fun saveDiceOneFace(faceId: String)
    suspend fun getDiceOneFace(): String?
}

expect fun createUserPreferencesDataStore(): UserPreferencesDataStore
